import { useEffect, useState } from 'react';
import Front from '@frontapp/plugin-sdk';
import { demoExclusif } from '../demo';

/**
 * Types minimalistes du SDK Front : on ne déclare que les champs utilisés ici,
 * pour rester compatible avec les évolutions du SDK.
 */
export interface ContexteFront {
  type: 'singleConversation' | 'noConversation' | 'multiConversations' | 'messageComposer';
  conversation?: { id: string; subject?: string };
  teammate?: { id?: string; name?: string; email?: string };
  openUrl?: (url: string) => void;
}

/**
 * Contexte de secours.
 *
 * Hors de l'iframe de Front, `contextUpdates` n'émet jamais : sans ce repli,
 * le plugin resterait sur son squelette de chargement et ne serait ni
 * développable ni montrable hors de Front.
 *
 * Le repli n'existe qu'en développement (`vite dev`) et en mode démonstration
 * — deux situations où l'interface est de toute façon annoncée comme telle.
 * Un build de production branché sur la vraie API ne l'embarque pas.
 *
 * Le cas à afficher se choisit dans l'URL :
 *   http://localhost:5174/?cnv=cnv_perime&tea=tea_4qlhk
 */
function contexteDeDeveloppement(): ContexteFront {
  const parametres = new URLSearchParams(window.location.search);
  const cnvId = parametres.get('cnv') ?? 'cnv_1bktghfy';
  return {
    type: cnvId === 'aucune' ? 'noConversation' : 'singleConversation',
    conversation: cnvId === 'aucune' ? undefined : { id: cnvId },
    teammate: { id: parametres.get('tea') ?? 'tea_4qlhk', name: 'Développement' },
  };
}

/** Délai laissé à Front pour se manifester avant de basculer sur le repli. */
const DELAI_REPLI_MS = 1200;

export function useContexteFront(): ContexteFront | undefined {
  const [contexte, setContexte] = useState<ContexteFront>();

  useEffect(() => {
    let recu = false;

    const abonnement = Front.contextUpdates.subscribe((suivant) => {
      recu = true;
      setContexte(suivant as unknown as ContexteFront);
    });

    let repli: number | undefined;
    if (import.meta.env.DEV || demoExclusif) {
      repli = window.setTimeout(() => {
        if (!recu) setContexte(contexteDeDeveloppement());
      }, DELAI_REPLI_MS);
    }

    return () => {
      abonnement.unsubscribe();
      if (repli) window.clearTimeout(repli);
    };
  }, []);

  return contexte;
}

/**
 * Ouvre un lien hors de l'iframe. Front expose `openUrl` ; en développement
 * hors de Front, on retombe sur `window.open`.
 */
export function ouvrirLien(contexte: ContexteFront | undefined, url: string): void {
  if (contexte?.openUrl) contexte.openUrl(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * URL d'ouverture d'une conversation Front.
 * `/relances` ne porte pas de `lien_front` — contrairement à `/todo` — et
 * l'identifiant `cnv_id` suffit à composer l'URL publique de Front.
 */
export function urlConversationFront(cnvId: string): string {
  return `https://app.frontapp.com/open/${encodeURIComponent(cnvId)}`;
}
